import { useEffect, useRef } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";
import { Selection } from "./useChatData";
import { addActivityItem } from "./activity";
import { mentionsUid } from "./format";
import { messageBelongsToSelection } from "./messageMatch";

const LISTENER_ID = "dbm-activity-logger";

/** Logs new-message/mention activity while the app is open — the message
 * listener half of useNotifications, but persisted to the Activity tab
 * instead of (or as well as) a toast. Logs regardless of which
 * conversation is open, since this is a history log, not an alert — but
 * an item for the conversation you already had open is tagged seenLive so
 * it doesn't ALSO count toward the unseen badge; you were already looking
 * straight at it. */
export function useActivityLogger(currentSelection: Selection | null) {
  const selectionRef = useRef(currentSelection);
  selectionRef.current = currentSelection;

  useEffect(() => {
    const onMessage = (msg: CometChat.BaseMessage) => {
      const meUid = CometChatUIKitLoginListener.getLoggedInUser()?.getUid();
      const meName = CometChatUIKitLoginListener.getLoggedInUser()?.getName();
      const sender = msg.getSender();
      if (!sender || sender.getUid() === meUid) return;

      const isGroup = msg.getReceiverType() === "group";
      let conversation: Selection;
      if (isGroup) {
        const receiver = msg.getReceiver();
        const name = receiver && "getName" in receiver ? (receiver as CometChat.Group).getName() : msg.getReceiverId();
        conversation = { kind: "group", id: msg.getReceiverId(), name, subtitle: "Channel" };
      } else {
        conversation = { kind: "user", id: sender.getUid(), name: sender.getName(), subtitle: "Direct message" };
      }

      const text = msg.getType() === "text" ? (msg as CometChat.TextMessage).getText() || "" : "Sent an attachment";
      // Prefer the server-side mention list the sender registered — the
      // first-name regex below is only a fallback for messages sent before
      // mentions were wired up (it false-positives on common first names).
      const mentioned = mentionsUid(msg as any, meUid)
        ? true
        : !!meName && new RegExp(`@${meName.split(" ")[0]}\\b`, "i").test(text);

      addActivityItem({
        kind: mentioned ? "mention" : "message",
        text: text.length > 100 ? text.slice(0, 97) + "…" : text,
        from: sender.getName(),
        conversation: { kind: conversation.kind, id: conversation.id, name: conversation.name },
        seenLive: messageBelongsToSelection(msg, selectionRef.current),
      });
    };

    CometChat.addMessageListener(
      LISTENER_ID,
      new CometChat.MessageListener({
        onTextMessageReceived: onMessage,
        onMediaMessageReceived: onMessage,
      })
    );
    return () => CometChat.removeMessageListener(LISTENER_ID);
  }, []);
}
